import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import collectionLogo from "../../assets/collection-icon.svg";
import {
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";
import Googlemap2 from "../common/GoogleMap/Googlemap2";
import CollectionPropertyCard from "./CollectionPropertyCard";
import {
  formatInspectionHeadingDate,
  parseInspectionDateKey,
} from "../../utils/inspectionDate";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getInspectionShowDate = (entry) =>
  entry?.show_date ?? entry?.date ?? entry?.inspection_date ?? null;

const getInspectionProperty = (entry) =>
  entry?.property ?? entry?.propertyId ?? {};

const normalizePropertyForMap = (prop) => {
  const base = { ...prop };
  const existing =
    base?.coordinates ??
    base?.location?.coordinates ??
    base?.geoLocation?.coordinates;
  if (Array.isArray(existing) && existing.length >= 2) return base;

  const lat = Number(base?.lat ?? base?.latitude);
  const lng = Number(
    base?.lng ?? base?.longitude ?? base?.lon ?? base?.long,
  );
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { ...base, coordinates: [lng, lat] };
  }
  return base;
};

function getDateRange(startDate, count) {
  const dates = [];
  const d = new Date(startDate);
  for (let i = 0; i < count; i++) {
    dates.push({
      date: d.getDate(),
      day: DAY_NAMES[d.getDay()],
      full: new Date(d),
    });
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

const InspectionsCollection = () => {

  const [dateStripStart, setDateStripStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [savedInspections, setSavedInspections] = useState([]);
  const [savedInspectionsLoading, setSavedInspectionsLoading] = useState(false);
  const stripRef = useRef(null);
  const FILTER_TABS = ["All", "Buy", "Rent", "Sold", "Inspected"];
  const [filter, setFilter] = useState("All");

  function getPropertyStatus(p) {
    const s = (
      p?.property?.status ||
      p?.propertyId?.status ||
      ""
    )
      .toString()
      .toLowerCase();
    if (s.includes("sold")) return "Sold";
    if (s.includes("active")) return "Buy";
    if (s.includes("rent") || s.includes("lease")) return "Rent";
    if (s.includes("inspect")) return "Inspected";
    return "Buy";
  }


  const toDateKey = (value) => parseInspectionDateKey(value);

  const fetchSavedInspections = async () => {
    const token = localStorage.getItem("Token");
    if (!token) return;
    setSavedInspectionsLoading(true);
    try {
      const res = await axiosInstanceAuth.get("/savedInspection");
      const list = res?.data?.data ?? res?.data ?? [];
      setSavedInspections(Array.isArray(list) ? list : []);
    } catch {
      setSavedInspections([]);
    } finally {
      setSavedInspectionsLoading(false);
    }
  };

  const inspectionDateKeySet = useMemo(() => {
    const set = new Set();
    savedInspections.forEach((entry) => {
      const key = toDateKey(getInspectionShowDate(entry));
      if (key) set.add(key);
    });
    return set;
  }, [savedInspections]);

  const filteredProperties = useMemo(() => {
    if (!Array.isArray(savedInspections)) return [];
    return savedInspections.filter((p) => {
      const statusOk =
        filter === "All" ? true : getPropertyStatus(p) === filter;
      if (!selectedDateKey) return statusOk;
      return statusOk && toDateKey(getInspectionShowDate(p)) === selectedDateKey;
    });
  }, [savedInspections, filter, selectedDateKey]);

  const mapLocations = useMemo(
    () =>
      filteredProperties
        .map((entry) => {
          const prop = normalizePropertyForMap(getInspectionProperty(entry));
          return {
            ...prop,
            _id: prop?._id ?? prop?.id ?? entry?._id ?? entry?.id,
          };
        })
        .filter((prop) => {
          const coords =
            prop?.coordinates ??
            prop?.location?.coordinates ??
            prop?.geoLocation?.coordinates;
          return Array.isArray(coords) && coords.length >= 2;
        }),
    [filteredProperties],
  );

  useEffect(() => {
    fetchSavedInspections();
  }, []);

  useEffect(() => {
    if (!inspectionDateKeySet.size) return;

    const inspectionDates = [...inspectionDateKeySet].map((key) => {
      const [year, month, day] = key.split("-").map(Number);
      return new Date(year, month - 1, day);
    });

    const earliest = new Date(
      Math.min(...inspectionDates.map((d) => d.getTime())),
    );
    const latest = new Date(
      Math.max(...inspectionDates.map((d) => d.getTime())),
    );
    const stripStart = new Date(dateStripStart);
    const stripEnd = new Date(stripStart);
    stripEnd.setDate(stripEnd.getDate() + 20);

    if (earliest < stripStart || latest > stripEnd) {
      const nextStart = new Date(earliest);
      nextStart.setDate(nextStart.getDate() - 3);
      setDateStripStart(nextStart);
    }
  }, [savedInspections, inspectionDateKeySet]);

  const dateStripDates = useMemo(() => {
    const range = getDateRange(new Date(dateStripStart), 21);
    const keysInRange = new Set(
      range.map((item) => toDateKey(item.full)),
    );

    savedInspections.forEach((entry) => {
      const key = toDateKey(getInspectionShowDate(entry));
      if (!key || keysInRange.has(key)) return;
      const [year, month, day] = key.split("-").map(Number);
      const full = new Date(year, month - 1, day);
      range.push({
        date: full.getDate(),
        day: DAY_NAMES[full.getDay()],
        full,
      });
      keysInRange.add(key);
    });

    range.sort((a, b) => a.full.getTime() - b.full.getTime());
    return range;
  }, [dateStripStart, savedInspections]);

  const scrollStrip = (dir) => {
    const step = 7;
    setDateStripStart((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + (dir === "next" ? step : -step));
      return next;
    });
  };

  return (
    <div className="bg-[#F3F6F9] py-10">
      <div className="container mx-auto">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="inline-flex items-center gap-1 text-[#097FE6] font-medium hover:underline"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          Back to collection
        </button>
        <h2 className="text-2xl font-semibold text-[#000929] mb-3">
          All inspections and auctions
        </h2>

        <div className="flex items-center gap-2 mb-6">
          <button
            type="button"
            onClick={() => scrollStrip("prev")}
            className="p-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0"
            aria-label="Previous dates"
          >
            <ChevronLeft
              className="w-5 h-5 text-[#097FE6]"
              strokeWidth={3}
            />
          </button>
          <div
            ref={stripRef}
            className="flex gap-1 overflow-x-auto scroll-smooth py-2 scrollbar-hide flex-1"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {/* \"All\" pill */}
            <button
              type="button"
              onClick={() => setSelectedDateKey(null)}
              className={`shrink-0 min-w-[64px] px-4 rounded-xl text-sm font-medium transition-colors ${selectedDateKey === null
                ? "bg-[#097FE6] text-white shadow-sm"
                : "bg-white text-[#000929] border border-gray-200 hover:bg-gray-50"
                }`}
            >
              All
            </button>
            {dateStripDates.map((item) => {
              const key = `${item.full.getFullYear()}-${String(
                item.full.getMonth() + 1,
              ).padStart(2, "0")}-${String(item.full.getDate()).padStart(
                2,
                "0",
              )}`;
              const isSelected = selectedDateKey === key;
              const hasInspectionForDay = inspectionDateKeySet.has(key);
              const baseClasses =
                "shrink-0 flex flex-col gap-1 items-center justify-center min-w-[70px] min-h-[90px] py-2 px-2 rounded-xl border text-center transition-colors";
              const stateClasses = isSelected
                ? "bg-[#097FE6] text-white border-[#097FE6] shadow-sm"
                : hasInspectionForDay
                  ? "bg-white text-[#000929] border-gray-200 shadow-sm"
                  : "bg-transparent text-gray-400 border-transparent opacity-50 hover:opacity-70";
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setSelectedDateKey(key)}
                  className={`${baseClasses} ${stateClasses}`}
                >
                  <span className="text-xl ">{item.date}</span>
                  <span className="text-base">{item.day}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => scrollStrip("next")}
            className="p-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0"
            aria-label="Next dates"
          >
            <ChevronRight
              className="w-5 h-5 text-[#097FE6]"
              strokeWidth={3}
            />
          </button>
        </div>
        {!localStorage.getItem("Token") ? (
          <div className="rounded-3xl bg-[#E9EDF1] border border-gray-200 min-h-[430px] flex flex-col items-center gap-6 justify-center px-6 py-12 text-center">
            <div className="mb-5 flex items-center justify-center">
              <img
                src={collectionLogo}
                alt="Collections"
                className="w-14 h-14"
              />
            </div>
            <p className="text-[#000929] font-semibold text-base sm:text-3xl mb-6">
              Whenever you add an event to your <br /> plan, it&apos;ll
              show up here.
            </p>
            <Link
              to="/inspection"
              className="inline-flex items-center gap-2 rounded-full bg-[#097FE6] px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#0664b7] transition-colors"
            >
              See All Inspection Times
              <ChevronRight
                className="w-4 h-4 text-white"
                strokeWidth={3}
              />
            </Link>
          </div>
        ) : savedInspectionsLoading ? (
          <div className="rounded-3xl bg-white border border-gray-200 min-h-[200px] flex items-center justify-center px-6 py-12">
            <p className="text-gray-500">Loading saved inspections...</p>
          </div>
        ) : savedInspections.length === 0 ? (
          <div className="rounded-3xl bg-[#E9EDF1] border border-gray-200 min-h-[430px] flex flex-col items-center gap-6 justify-center px-6 py-12 text-center">
            <div className="mb-5 flex items-center justify-center">
              <img
                src={collectionLogo}
                alt="Collections"
                className="w-14 h-14"
              />
            </div>
            <p className="text-[#000929] font-semibold text-base sm:text-3xl mb-6">
              Whenever you add an event to your <br /> plan, it&apos;ll
              show up here.
            </p>
            <Link
              to="/inspection"
              className="inline-flex items-center gap-2 rounded-full bg-[#097FE6] px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#0664b7] transition-colors"
            >
              See All Inspection Times
              <ChevronRight
                className="w-4 h-4 text-white"
                strokeWidth={3}
              />
            </Link>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            <div className="rounded-2xl overflow-hidden border border-[#EAEAEA] bg-white">
              {mapLocations.length > 0 ? (
                <Googlemap2
                  key={`inspection-map-${selectedDateKey ?? "all"}-${filter}-${mapLocations.length}`}
                  location={mapLocations}
                  style={{ height: "400px", width: "100%" }}
                />
              ) : (
                <div className="h-[280px] sm:h-[400px] flex items-center justify-center bg-[#F3F6F9] text-sm text-gray-500 px-6 text-center">
                  No property locations to show for this selection.
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setFilter(tab)}
                  className={`shrink-0 rounded-full px-5 py-2 text-sm font-medium transition text-[#000929] ${filter === tab
                    ? "bg-white border-2 border-[#000929]"
                    : "bg-white border border-gray-200 hover:border-gray-300"
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {filteredProperties.length === 0 ? (
              <div className="rounded-2xl bg-white border border-[#EAEAEA] px-6 py-12 text-center text-gray-500">
                {selectedDateKey
                  ? "No inspections scheduled for this date."
                  : "No inspections match this filter."}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProperties.map((item) => {
                  const prop = getInspectionProperty(item);
                  const id = item._id ?? item.id ?? prop?._id ?? prop?.id;
                  const showTimeHeader = selectedDateKey !== null;
                  const showDate = getInspectionShowDate(item);
                  return (
                    <div key={id} className="flex flex-col gap-3">
                      {showTimeHeader && (
                        <div className="bg-[#F3F6F9] rounded-2xl px-4 py-3">
                          <p className="text-2xl sm:text-3xl font-semibold text-[#000929]">
                            {formatInspectionHeadingDate(showDate)}
                          </p>
                          <p className="text-lg sm:text-xl text-[#000929] mt-4 font-semibold">
                            Open times
                          </p>
                          {item.startTime && item.endTime && (
                            <p className="text-sm font-medium text-[#000929] mt-2">
                              {`${item.startTime.toLowerCase()} – ${item.endTime.toLowerCase()}`}
                            </p>
                          )}
                        </div>
                      )}
                      <CollectionPropertyCard
                        property={prop}
                        isFavorite={true}
                        isAuthenticate={true}
                        setAuthPopUp={() => { }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default InspectionsCollection